import React, { useState, useEffect } from "react";
import { apiService } from "../../../services/api";
import { AlertRule } from "../../../types";
import LoadingSpinner from "../../../components/common/LoadingSpinner";

export default function CompanyAlertsTab() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAlertRules();
      if (response.success) {
        setRules(response.data);
      }
    } catch (err) {
      setError("Erro ao carregar regras de alerta");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRuleChange = (id: string, field: keyof AlertRule, value: any) => {
    setRules(
      rules.map((rule) => (rule.id === id ? { ...rule, [field]: value } : rule))
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");
      await apiService.updateAlertRules(rules);
      setSuccessMessage("Regras de alerta atualizadas com sucesso!");
    } catch (err) {
      setError("Erro ao salvar regras");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
          Configuração de Alertas
        </h2>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isSaving ? "Salvando..." : "Salvar Alterações"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          {successMessage}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Tipo de Alerta
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Severidade
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Configuração
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {rule.alert_type === "offline"
                    ? "OFFLINE (Inatividade)"
                    : rule.alert_type === "off_hours_usage"
                    ? "Uso Fora do Horário"
                    : rule.alert_type.replace(/_/g, " ").toUpperCase()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={rule.severity}
                    onChange={(e) =>
                      handleRuleChange(rule.id, "severity", e.target.value)
                    }
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">
                  {rule.alert_type === "offline" ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        value={
                          rule.threshold ? Math.round(rule.threshold / 60) : 72
                        }
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          handleRuleChange(
                            rule.id,
                            "threshold",
                            val > 0 ? val * 60 : 60
                          );
                        }}
                        className="w-20 shadow-sm focus:ring-blue-500 focus:border-blue-500 block sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      <span>horas</span>
                    </div>
                  ) : rule.alert_type === "off_hours_usage" ? (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Horário de Brasília
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          Permitido:
                        </label>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <label className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              Início:
                            </label>
                            <input
                              type="time"
                              value={
                                rule.configuration?.start_time || "08:00"
                              }
                              onChange={(e) => {
                                handleRuleChange(rule.id, "configuration", {
                                  ...rule.configuration,
                                  start_time: e.target.value,
                                  end_time: rule.configuration?.end_time || "18:00",
                                });
                              }}
                              className="w-20 shadow-sm focus:ring-blue-500 focus:border-blue-500 block sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                          <div className="flex items-center space-x-1">
                            <label className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              Fim:
                            </label>
                            <input
                              type="time"
                              value={
                                rule.configuration?.end_time || "18:00"
                              }
                              onChange={(e) => {
                                handleRuleChange(rule.id, "configuration", {
                                  ...rule.configuration,
                                  start_time: rule.configuration?.start_time || "08:00",
                                  end_time: e.target.value,
                                });
                              }}
                              className="w-20 shadow-sm focus:ring-blue-500 focus:border-blue-500 block sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <button
                      onClick={() =>
                        handleRuleChange(
                          rule.id,
                          "is_enabled",
                          !rule.is_enabled
                        )
                      }
                      className={`${
                        rule.is_enabled ? "bg-blue-600" : "bg-gray-200"
                      } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                    >
                      <span
                        aria-hidden="true"
                        className={`${
                          rule.is_enabled ? "translate-x-5" : "translate-x-0"
                        } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                      />
                    </button>
                    <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                      {rule.is_enabled ? "Ativado" : "Desativado"}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
